// ─── pages/MyCards.jsx ───────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from "react";
import config from "../config";
import Swal from "sweetalert2";
import { checklistProgress } from "../store.js";
import { C, LabelPill, ChecklistBar, CardModal } from "../Create_plan_Trello/Shared";

// ─── Swal helpers ─────────────────────────────────────────────────────────────
const swalBase = {
  background: "#1e293b", color: "#f1f5f9",
  confirmButtonColor: "#579dff", cancelButtonColor: "#334155",
  customClass: { popup: "swb-popup" },
};
const swalConfirm = async (title, text = "", confirmText = "Delete") =>
  (await Swal.fire({
    ...swalBase, title, text, icon: "warning", showCancelButton: true,
    confirmButtonText: confirmText, cancelButtonColor: "#334155",
    confirmButtonColor: "#ef4444",
  })).isConfirmed;
const swalSuccess = (title) =>
  Swal.fire({ ...swalBase, icon: "success", title, timer: 1500, showConfirmButton: false });
const swalError = (text) =>
  Swal.fire({ ...swalBase, icon: "error", title: "Oops!", text });

// ─── Current user ─────────────────────────────────────────────────────────────
const getUser = () => {
  try {
    const admin = localStorage.getItem("admin");
    if (admin) { const p = JSON.parse(admin); return p.username || p.fullNames || "System"; }
    const user = localStorage.getItem("user");
    if (user) return JSON.parse(user).username || "System";
  } catch {}
  return "System";
};

// ─── Card parsers ─────────────────────────────────────────────────────────────
const fromApiCard = (card) => ({
  ...card,
  desc: card.description ?? "",
  labels: Array.isArray(card.labels)
    ? card.labels
    : (card.labels ? card.labels.split(",").map(s => s.trim()).filter(Boolean) : []),
  checklist: (() => {
    if (Array.isArray(card.checklist)) return card.checklist;
    try { return JSON.parse(card.checklist || "[]"); } catch { return []; }
  })(),
  status: card.status ?? "todo",
});

const toApiCard = (card) => ({
  id:          card.id ?? 0,
  colId:       card.colId,
  title:       card.title?.trim() || "",
  description: card.desc ?? card.description ?? "",
  labels:      Array.isArray(card.labels) ? card.labels.join(",") : (card.labels ?? ""),
  checklist:   Array.isArray(card.checklist) ? JSON.stringify(card.checklist) : (card.checklist ?? "[]"),
  archived:    card.archived ?? false,
  status:      card.status ?? "todo",
  createdBy:   card.createdBy || getUser(),
  createdAt:   card.createdAt
    ? (typeof card.createdAt === "number" ? new Date(card.createdAt).toISOString() : card.createdAt)
    : new Date().toISOString(),
});

// ─── Status config ─────────────────────────────────────────────────────────────
const STATUS_LABELS = {
  todo:       { label: "To Do",       color: "#579dff", emoji: "📋" },
  inprogress: { label: "In Progress", color: "#faa53d", emoji: "⚙️"  },
  pending:    { label: "Pending",     color: "#f87168", emoji: "⏳" },
  complete:   { label: "Complete",    color: "#4bce97", emoji: "✅" },
};

// ─── Attachment download base ─────────────────────────────────────────────────
const ATT_BASE = `${config.API_BASE_URL}/cards/attachments`;

// ─── useCoverAndCount — fetches attachments per card ─────────────────────────
// Returns { coverUrl, count } for a given cardId
// Results are cached in a module-level Map to avoid re-fetching
const attCache = new Map(); // cardId → { coverUrl, count }

function useCoverAndCount(cardId) {
  const [data, setData] = useState(attCache.get(cardId) ?? { coverUrl: null, count: 0 });

  useEffect(() => {
    if (!cardId) return;
    if (attCache.has(cardId)) { setData(attCache.get(cardId)); return; }

    let cancelled = false;
    fetch(`${config.API_BASE_URL}/cards/${cardId}/attachments`)
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        if (cancelled) return;
        const firstImage = list.find(a => a.contentType?.startsWith("image/"));
        const result = {
          coverUrl: firstImage ? `${ATT_BASE}/${firstImage.id}/download` : null,
          count: list.length,
        };
        attCache.set(cardId, result);
        setData(result);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cardId]);

  return data;
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: "80px 0" }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #1a3050", borderTop: "3px solid #579dff", animation: "mc-spin .8s linear infinite" }} />
      <span style={{ color: "#4a6a8a", fontSize: 13 }}>Loading your cards…</span>
    </div>
  );
}

// ─── Single Card ──────────────────────────────────────────────────────────────
function MyCardItem({ card, getColName, getColColor, onClick }) {
  const [hov, setHov]       = useState(false);
  const progress            = checklistProgress(card.checklist);
  const st                  = STATUS_LABELS[card.status] ?? STATUS_LABELS.todo;
  const { coverUrl, count } = useCoverAndCount(card.id);
  const colColor            = getColColor(card.colId);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:   hov ? C.surfaceHov : C.surface,
        border:       `1px solid ${hov ? C.borderHov : C.border}`,
        borderRadius: 10,
        cursor:       "pointer",
        transition:   "all .18s",
        transform:    hov ? "translateY(-2px)" : "none",
        boxShadow:    hov ? "0 8px 24px rgba(0,0,0,.45)" : "0 1px 4px rgba(0,0,0,.2)",
        overflow:     "hidden",
      }}
    >
      {/* ── Cover image ───────────────────────────────────────────── */}
      {coverUrl && (
        <div style={{ width: "100%", height: 140, overflow: "hidden", position: "relative", background: "#0a1628", flexShrink: 0 }}>
          <img
            src={coverUrl}
            alt="cover"
            style={{
              width: "100%", height: "100%", objectFit: "cover", display: "block",
              transition: "transform .3s",
              transform: hov ? "scale(1.04)" : "scale(1)",
            }}
          />
          {/* Attachment count badge on cover */}
          {count > 0 && (
            <div style={{
              position: "absolute", bottom: 8, right: 8,
              background: "rgba(0,0,0,.65)", backdropFilter: "blur(4px)",
              borderRadius: 20, padding: "3px 8px",
              display: "flex", alignItems: "center", gap: 4,
              fontSize: 11, color: "#fff", fontWeight: 600,
            }}>
              📎 {count}
            </div>
          )}
          {/* Status badge on cover */}
          <div style={{ position: "absolute", top: 8, left: 8 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: st.color,
              background: `rgba(0,0,0,.6)`, backdropFilter: "blur(4px)",
              padding: "3px 8px", borderRadius: 20,
              border: `1px solid ${st.color}66`,
              display: "inline-flex", alignItems: "center", gap: 3,
            }}>
              {st.emoji} {st.label}
            </span>
          </div>
        </div>
      )}

      {/* ── Card body ─────────────────────────────────────────────── */}
      <div style={{ padding: "12px 14px" }}>

        {/* Labels */}
        {card.labels.length > 0 && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
            {card.labels.map(l => <LabelPill key={l} color={l} small />)}
          </div>
        )}

        {/* Title */}
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, lineHeight: 1.45, marginBottom: 8 }}>
          {card.title}
        </div>

        {/* Description preview */}
        {card.desc && (
          <div style={{
            fontSize: 11, color: C.textMuted, marginBottom: 8, lineHeight: 1.5,
            display: "-webkit-box", WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {card.desc}
          </div>
        )}

        {/* Checklist progress */}
        {progress && (
          <div style={{ marginBottom: 8 }}>
            <ChecklistBar pct={progress.pct} />
            <span style={{ fontSize: 10, color: progress.pct === 100 ? "#4bce97" : C.textDim, marginTop: 3, display: "block" }}>
              ✓ {card.checklist.filter(i => i.done).length}/{card.checklist.length} tasks
            </span>
          </div>
        )}

        {/* Footer */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`,
        }}>
          {/* Status badge (only if no cover image) */}
          {!coverUrl && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: st.color,
              background: `${st.color}18`, padding: "2px 8px",
              borderRadius: 20, border: `1px solid ${st.color}33`,
              display: "inline-flex", alignItems: "center", gap: 3,
            }}>
              {st.emoji} {st.label}
            </span>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: coverUrl ? 0 : "auto" }}>
            {/* Attachment count (only if no cover — already shown on cover) */}
            {!coverUrl && count > 0 && (
              <span style={{
                fontSize: 11, color: C.textDim,
                background: C.surface, padding: "2px 7px",
                borderRadius: 20, border: `1px solid ${C.border}`,
                display: "inline-flex", alignItems: "center", gap: 3,
              }}>
                📎 {count}
              </span>
            )}
            {/* Description icon */}
            {card.desc && !coverUrl && (
              <span style={{ fontSize: 11, color: C.textDim }}>📝</span>
            )}
            {/* Column badge */}
            <span style={{
              fontSize: 10, fontWeight: 600, color: colColor,
              background: `${colColor}1a`, padding: "2px 8px", borderRadius: 20,
            }}>
              {getColName(card.colId)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MyCards() {
  const [columns,  setColumns]  = useState([]);
  const [cards,    setCards]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [openCard, setOpenCard] = useState(null);
  const [filter,   setFilter]   = useState("all");
  const [search,   setSearch]   = useState("");

  // ── Fetch from API ─────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    attCache.clear(); // clear cover cache on refresh
    try {
      const currentUser = getUser();
      const [colsRes, cardsRes] = await Promise.all([
        fetch(`${config.API_BASE_URL}/boards`),
        fetch(`${config.API_BASE_URL}/cards`),
      ]);
      const allCols  = colsRes.ok  ? await colsRes.json()  : [];
      const allCards = cardsRes.ok ? await cardsRes.json() : [];

      const userCols = allCols.filter(c => c.createdBy === currentUser);
      setColumns(userCols);

      const userColIds = new Set(userCols.map(c => c.id));
      const myCards = allCards
        .map(fromApiCard)
        .filter(c => !c.archived && (userColIds.has(c.colId) || c.createdBy === currentUser));
      setCards(myCards);
    } catch {
      await swalError("Failed to load cards.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const updateCard = async (updated) => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${updated.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toApiCard(updated)),
      });
      if (!res.ok) throw new Error();
      const saved = fromApiCard(await res.json());
      attCache.delete(saved.id); // invalidate cover cache
      setCards(p => p.map(c => c.id === saved.id ? saved : c));
      if (openCard?.id === saved.id) setOpenCard(saved);
    } catch { await swalError("Failed to update card."); }
  };

  const archiveCard = async (id) => {
    const card = cards.find(c => c.id === id);
    if (!card) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toApiCard({ ...card, archived: true })),
      });
      if (!res.ok) throw new Error();
      attCache.delete(id);
      setCards(p => p.filter(c => c.id !== id));
      setOpenCard(null);
      await swalSuccess("Card archived! 🗃");
    } catch { await swalError("Failed to archive card."); }
  };

  const deleteCard = async (id) => {
    if (!await swalConfirm("Delete card?", "This cannot be undone.", "Yes, delete it")) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      attCache.delete(id);
      setCards(p => p.filter(c => c.id !== id));
      setOpenCard(null);
    } catch { await swalError("Failed to delete card."); }
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const FILTERS = [
    { id: "all",        label: "All Cards"      },
    { id: "todo",       label: "📋 To Do"       },
    { id: "inprogress", label: "⚙️ In Progress"  },
    { id: "pending",    label: "⏳ Pending"      },
    { id: "complete",   label: "✅ Complete"     },
  ];

  const displayed = cards.filter(card => {
    const matchFilter = filter === "all" || card.status === filter;
    const matchSearch = !search.trim() ||
      card.title.toLowerCase().includes(search.toLowerCase()) ||
      (card.desc ?? "").toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const getColName  = (id) => columns.find(c => c.id === id)?.title || "—";
  const getColColor = (id) => columns.find(c => c.id === id)?.color || C.textDim;

  const stats = {
    total:      cards.length,
    todo:       cards.filter(c => c.status === "todo").length,
    inprogress: cards.filter(c => c.status === "inprogress").length,
    pending:    cards.filter(c => c.status === "pending").length,
    complete:   cards.filter(c => c.status === "complete").length,
  };

  return (
    <>
      <style>{`
        @keyframes mc-spin { to { transform: rotate(360deg); } }
        .swb-popup { border: 1px solid #1e3a55 !important; border-radius: 16px !important; }
      `}</style>

      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>🗂</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>My Cards</span>
            {!loading && (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#4a6a8a", background: "#152438", padding: "2px 9px", borderRadius: 12, border: "1px solid #1a3050" }}>
                {displayed.length}
              </span>
            )}
            <button onClick={fetchData} disabled={loading} title="Refresh"
              style={{ background: "transparent", border: "1px solid #1a3050", borderRadius: 7, color: "#6b8caf", fontSize: 14, padding: "3px 10px", cursor: loading ? "default" : "pointer", fontFamily: "inherit", opacity: loading ? 0.4 : 1, transition: "all .15s" }}>
              ↻
            </button>
          </div>

          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍  Search cards…"
            style={{ background: C.surface, border: "1px solid #1a3050", borderRadius: 8, padding: "8px 14px", fontSize: 13, color: C.text, outline: "none", fontFamily: "inherit", width: 220, transition: "border-color .15s" }}
            onFocus={e => e.target.style.borderColor = "#579dff"}
            onBlur={e => e.target.style.borderColor = "#1a3050"}
          />
        </div>

        {/* ── Stats Bar ──────────────────────────────────────────────── */}
        {!loading && cards.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {[
              { label: "Total",       color: "#579dff", val: stats.total      },
              { label: "To Do",       color: "#579dff", val: stats.todo       },
              { label: "In Progress", color: "#faa53d", val: stats.inprogress },
              { label: "Pending",     color: "#f87168", val: stats.pending    },
              { label: "Complete",    color: "#4bce97", val: stats.complete   },
            ].map(s => (
              <div key={s.label} style={{ background: `${s.color}12`, border: `1px solid ${s.color}30`, borderRadius: 10, padding: "8px 14px", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 70 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</span>
                <span style={{ fontSize: 10, color: "#6b8caf", fontWeight: 600 }}>{s.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Filter Tabs ────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
          {FILTERS.map(f => {
            const active = filter === f.id;
            const count  = f.id === "all" ? cards.length : cards.filter(c => c.status === f.id).length;
            return (
              <div key={f.id} onClick={() => setFilter(f.id)} style={{ padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: active ? 700 : 400, color: active ? "#fff" : C.textMuted, background: active ? C.accent : C.surface, border: `1px solid ${active ? C.accent : C.border}`, transition: "all .12s", display: "flex", alignItems: "center", gap: 5 }}>
                {f.label}
                <span style={{ fontSize: 10, opacity: 0.75, background: active ? "rgba(255,255,255,.2)" : C.surface, padding: "1px 5px", borderRadius: 10 }}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── Loading ────────────────────────────────────────────────── */}
        {loading && <Spinner />}

        {/* ── Empty ──────────────────────────────────────────────────── */}
        {!loading && displayed.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>🗂</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#8aaccc", marginBottom: 6 }}>
              {search ? "No results found" : "No cards found"}
            </div>
            <div style={{ fontSize: 13, color: "#4a6a8a" }}>
              {search ? `No cards matching "${search}"` : "Create a card from the Board view to see it here."}
            </div>
          </div>
        )}

        {/* ── Cards grouped by column ────────────────────────────────── */}
        {!loading && displayed.length > 0 && columns.map(col => {
          const colCards = displayed.filter(c => c.colId === col.id);
          if (!colCards.length) return null;
          return (
            <div key={col.id} style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: col.color, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, boxShadow: `0 0 6px ${col.color}99` }} />
                {col.title}
                <span style={{ color: C.textDim, fontWeight: 400, textTransform: "none" }}>({colCards.length})</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
                {colCards.map(card => (
                  <MyCardItem
                    key={card.id}
                    card={card}
                    getColName={getColName}
                    getColColor={getColColor}
                    onClick={() => setOpenCard(card)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Card Modal ─────────────────────────────────────────────── */}
      {openCard && (
        <CardModal
          card={openCard}
          columns={columns}
          onClose={() => setOpenCard(null)}
          onUpdate={updateCard}
          onArchive={archiveCard}
          onDelete={deleteCard}
        />
      )}
    </>
  );
}